import { FrontendConstants } from '../common/FrontendConstants';
import { CommonConstants, GetCountryForTimezone, LocalizationCodeValues, Logger } from '@shared/common';
import type { LayoutValues } from '../types/LayoutValues';
import type { LocalizationCode } from '@shared/common';

/** File-local constants for Helpers. */
const localConstants = {
  logContext: 'Helpers',
  cssUnitPx: 'px',
  cssFontSizeProperty: 'font-size',
  rootElementId: 'root',
  localhost: 'localhost',
  localhostIp: '127.0.0.1',
  langSeparator: '-',
  urlPathSeparator: '/',
  // IndexedDB-backed key-value store used by helpers like SetIndexedDB / GetIndexedDB. Single database, single object store — callers namespace keys themselves (e.g. `translate:...`).
  idbDatabaseName: 'vertex-echo',
  idbStoreName: 'kv',
  idbVersion: 1,
  logIndexedDBGetErr: 'IndexedDB get failed: ',
  logIndexedDBSetErr: 'IndexedDB set failed: ',
  logIndexedDBDeleteErr: 'IndexedDB delete failed: ',
  // Known crawler/bot user-agent markers (self-identified): Googlebot and AdsBot (Google), Bingbot, and the other majors, plus headless renderers (Lighthouse, HeadlessChrome).
  botUserAgentPattern: /googlebot|adsbot|bingbot|slurp|duckduckbot|baiduspider|yandex|lighthouse|headlesschrome/i,
  // Network-level fetch failure messages per browser engine (Chrome / Firefox / Safari) - no status code, no server signal.
  networkErrorPattern: /failed to fetch|networkerror|load failed/i
} as const;

/**
 * Scales the root font size based on window size. Reference: 1536x730 = 16px. Scales proportionally and clamps between MinFontSize and MaxFontSize. Also enforces orientation-dependent minimum
 * dimensions on body and #root.
 *
 * @param portraitScaleFactor - Extra multiplier applied to the clamped size ONLY in portrait orientation. Defaults to 1 (no change), so apps like the portal keep the base size. The lobby passes
 *   `Constants.PortraitSpacingScale` so every rem-based dimension (typography, padding, margins, gaps) scales up uniformly when the device is held upright.
 */
export function SetRootFontSize(portraitScaleFactor: number = 1) {
  const { scaler, portrait } = GetLayout();
  const clampedFontSize = Math.max(FrontendConstants.MinFontSize, Math.min(FrontendConstants.MaxFontSize, FrontendConstants.ReferenceFontSize * scaler));
  // Apply the portrait boost after clamping so portrait can exceed MaxFontSize (mirrors the pre-paint inline scripts in the lobby's index.html / build.index.html).
  const fontSize = portrait ? clampedFontSize * portraitScaleFactor : clampedFontSize;
  document.documentElement.style.fontSize = fontSize + localConstants.cssUnitPx;

  // Enforce minimum dimensions so content overflows and clips instead of compressing.
  const minW = (portrait ? FrontendConstants.MinWidthPortrait : FrontendConstants.MinWidthLandscape) + localConstants.cssUnitPx;
  const minH = (portrait ? FrontendConstants.MinHeightPortrait : FrontendConstants.MinHeightLandscape) + localConstants.cssUnitPx;
  document.body.style.minWidth = minW;
  document.body.style.minHeight = minH;
  const root = document.getElementById(localConstants.rootElementId);
  if (root) {
    root.style.minWidth = minW;
    root.style.minHeight = minH;
  }

  Logger.Debug(localConstants.logContext, `Set root font size to ${fontSize.toFixed(2)}px (scale: ${scaler.toFixed(4)})`);
}

/**
 * Registers a callback to run whenever the visual viewport changes - resize, rotation, or mobile browser chrome show/hide.
 *
 * Subscribes to the full set of viewport-change events that exist across browsers:
 *
 * - `window.resize` - fires on desktop resize and after mobile rotation (sometimes prematurely on Android).
 * - `window.orientationchange` - fires on mobile rotation. On Android Chrome this fires BEFORE `innerWidth`/`innerHeight` finish updating, so the callback is also scheduled via
 *   `requestAnimationFrame` and a delayed `setTimeout(FrontendConstants.OrientationStabilizeMs)` to recompute once the viewport stabilises. Without this, rotating landscape to portrait leaves
 *   the page rendered against stale landscape `innerWidth` and produces an oversized layout (iPhone Safari is not affected because it fires `resize` only after the viewport settles).
 * - `visualViewport.resize` - fires when the visible viewport changes (browser chrome, on-screen keyboard, pinch zoom) on modern mobile browsers. Catches the post-rotation settle on Android even
 *   when `orientationchange` does not.
 *
 * @param callback - The function to invoke on every viewport change.
 * @returns A disposer that removes every listener registered by this call. Invoke it on teardown to avoid leaking handlers.
 */
export function RegisterViewportChange(callback: () => void): () => void {
  // Named handler so the disposer can remove the exact same reference (an inline arrow could not be removed).
  const orientationHandler = (): void => {
    requestAnimationFrame(callback);
    window.setTimeout(callback, FrontendConstants.OrientationStabilizeMs);
  };
  // Capture the viewport object so removal targets the same instance the listener was added to.
  const visualViewport = window.visualViewport;
  window.addEventListener('resize', callback);
  window.addEventListener('orientationchange', orientationHandler);
  if (visualViewport) {
    visualViewport.addEventListener('resize', callback);
  }
  return () => {
    window.removeEventListener('resize', callback);
    window.removeEventListener('orientationchange', orientationHandler);
    if (visualViewport) {
      visualViewport.removeEventListener('resize', callback);
    }
  };
}

/** @returns The root font size in pixels. */
export function GetRootFontSize(): number {
  const rootFontSize = window.getComputedStyle(document.documentElement).getPropertyValue(localConstants.cssFontSizeProperty);
  return parseFloat(rootFontSize);
}

/**
 * Converts rem units to pixel position based on current root font size.
 *
 * @param rem - The value in rem units.
 * @returns The equivalent value in pixels.
 */
export function GetPixelsFromRem(rem: number): number {
  const fontSizePx = GetRootFontSize();
  return rem * fontSizePx;
}

/**
 * Converts pixel position to rem units based on current root font size.
 *
 * @param px - The value in pixels.
 * @returns The equivalent value in rem units.
 */
export function GetRemFromPixels(px: number): number {
  const fontSizePx = GetRootFontSize();
  return px / fontSizePx;
}

/**
 * Generates an RFC4122 v4 UUID string safe for use as an idempotency key or correlation ID on the client.
 *
 * Prefers `crypto.randomUUID()` (modern secure contexts) and falls back to `crypto.getRandomValues()` so the call still works on non-secure origins (HTTP, in-app webviews, older browsers) where
 * `crypto.randomUUID` is undefined. Direct calls to `crypto.randomUUID()` throw `crypto.randomUUID is not a function` outside secure contexts; always use this helper instead.
 *
 * @returns A v4 UUID string in canonical `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx` form.
 */
export function GenerateClientUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  // Manual v4 via getRandomValues — works on insecure contexts where randomUUID is missing.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Per RFC4122 §4.4: set version (4) and variant (10xx) bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex: string[] = [];
  for (let i = 0; i < 16; i++) hex.push(bytes[i].toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

/** @returns The client IP or undefined if not available (used in Development mode for network access). */
export function GetClientIP(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const hostname = window.location.hostname;
  // Return the hostname/IP if it's not localhost, otherwise undefined to use default
  return hostname && hostname !== localConstants.localhost && hostname !== localConstants.localhostIp ? hostname : undefined;
}

/** Requests fullscreen mode for the document element. */
export function RequestFullscreen(): void {
  document.documentElement.requestFullscreen();
}

/** @returns If the document is currently in fullscreen mode. */
export function IsFullscreen(): boolean {
  return !!(document.fullscreenElement || document.webkitFullscreenElement);
}

/**
 * Detects iOS / iPadOS Safari (and any WebKit-based browser running on iOS, since iOS forces all browsers to use WebKit).
 *
 * Used to gate iOS-specific workarounds — most notably the auto-zoom-to-input behaviour Safari triggers when a focused `<input>` has a computed `font-size` below 16px. The rest of the app uses
 * rem-based adaptive sizing (root font-size is dynamically clamped 6-18px by `SetRootFontSize`), so on small iPhone viewports rem values resolve well below 16px and trigger the zoom — which iOS
 * then fails to undo when the keyboard dismisses. This helper lets callers conditionally apply a `max(<rem>, 16px)` font-size floor ONLY on iOS, preserving rem adaptivity everywhere else.
 *
 * Detection covers both legacy iPhone/iPad UAs and iPadOS 13+ which reports as Mac but exposes touch (maxTouchPoints > 1) — `navigator.platform` checks are unreliable on modern iPads.
 *
 * @returns `true` when running on iOS / iPadOS Safari (or any iOS WebKit shell), `false` otherwise (including Android, desktop browsers, SSR / no-window contexts).
 */
export function IsIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ — reports as Macintosh but supports multi-touch, which desktop macOS does not.
  if (ua.includes('Macintosh') && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) return true;
  return false;
}

/**
 * Calculates the scale factors based on the current window size.
 *
 * @returns An object containing scaleX and scaleY factors.
 */
export function GetLayout(): LayoutValues {
  const wWidth = window.innerWidth;
  const wHeight = window.innerHeight;
  const portrait = wWidth < wHeight;

  const refW = portrait ? FrontendConstants.ReferenceWidthPortrait : FrontendConstants.ReferenceWidthLandscape;
  const refH = portrait ? FrontendConstants.ReferenceHeightPortrait : FrontendConstants.ReferenceHeightLandscape;
  const minW = portrait ? FrontendConstants.MinWidthPortrait : FrontendConstants.MinWidthLandscape;
  const minH = portrait ? FrontendConstants.MinHeightPortrait : FrontendConstants.MinHeightLandscape;

  const canvasW = Math.max(wWidth, minW);
  const canvasH = Math.max(wHeight, minH);
  const scaleX = canvasW / refW;
  const scaleY = canvasH / refH;

  const scaler = portrait ? scaleX : scaleY;
  Logger.Debug(localConstants.logContext, `Layout calculated: w=${canvasW}, h=${canvasH}, scaleX=${scaleX.toFixed(2)}, scaleY=${scaleY.toFixed(2)}, scaler=${scaler.toFixed(2)}, portrait=${portrait}`);
  return { scaler, scaleX, scaleY, canvasW, canvasH, portrait };
}

/**
 * Checks whether a string is a valid LocalizationCode.
 *
 * @param value - The string to check.
 * @returns True if the value is a recognised LocalizationCode.
 */
export function IsValidLocalizationCode(value: string): boolean {
  return LocalizationCodeValues.includes(value);
}

/**
 * Derives the best matching LocalizationCode from the browser's language preferences.
 *
 * Iterates `navigator.languages` (falling back to `navigator.language`), checking for an exact match first, then a language-only prefix match (e.g. `fr` → `fr-FR`).
 *
 * @returns The best matching LocalizationCode, or the system default when no match is found.
 */
export function GetUserGeoLang(): LocalizationCode {
  const candidates = navigator.languages?.length ? [...navigator.languages] : [navigator.language];

  for (const candidate of candidates) {
    // Exact match (e.g. "en-US")
    if (IsValidLocalizationCode(candidate)) return candidate as LocalizationCode;

    // Language-only prefix match (e.g. "fr" → "fr-FR")
    const prefix = candidate.split(localConstants.langSeparator)[0].toLowerCase();
    const match = LocalizationCodeValues.find((code) => code.toLowerCase().startsWith(prefix + localConstants.langSeparator));
    if (match) return match as LocalizationCode;
  }

  return CommonConstants.Defaults.Localization;
}

/**
 * Reads the persisted language code from local storage and validates it.
 *
 * @returns The stored LocalizationCode, or null if absent or invalid.
 */
export function GetStoredLanguage(): LocalizationCode | null {
  const stored = localStorage.getItem(CommonConstants.Params.Lang);
  if (stored && IsValidLocalizationCode(stored)) return stored as LocalizationCode;
  return null;
}

/**
 * Persists a language code to local storage so it can be restored on subsequent visits.
 *
 * @param langCode - The LocalizationCode to store.
 */
export function SetStoredLanguage(langCode: LocalizationCode) {
  localStorage.setItem(CommonConstants.Params.Lang, langCode);
}

/**
 * Derives the user's language preference from URL parameters, local storage, or browser settings.
 *
 * @returns The user language code as a LocalizationCode.
 */
export function GetUserLanguage(): LocalizationCode {
  // Get the requested language from URL path or query parameter
  const pathSegments = window.location.pathname.split(localConstants.urlPathSeparator).filter(Boolean);
  const queryParams = new URLSearchParams(window.location.search);
  // Determine language code — validate every candidate before accepting
  let langCode: LocalizationCode | null = null;
  if (pathSegments.length > 0) {
    const candidate = pathSegments[pathSegments.length - 1];
    if (IsValidLocalizationCode(candidate)) langCode = candidate as LocalizationCode;
  }
  if (!langCode && queryParams.has(CommonConstants.Params.Lang)) {
    const candidate = queryParams.get(CommonConstants.Params.Lang);
    if (candidate && IsValidLocalizationCode(candidate)) langCode = candidate as LocalizationCode;
  }
  if (!langCode) {
    langCode = GetStoredLanguage();
  }
  // Fall back to browser geolocation language, then system default
  return langCode || GetUserGeoLang();
}

/**
 * Derives the user's country from the browser's detected timezone.
 *
 * Uses `Intl.DateTimeFormat().resolvedOptions().timeZone` to get the IANA timezone, then maps it to an ISO country code via the shared timezone database. Falls back to 'US' when the timezone is
 * unknown or the runtime lacks `Intl` support.
 *
 * @returns The ISO 3166-1 alpha-2 country code (uppercase).
 */
export function GetUserGeoCountry(): string {
  const tz = GetUserGeoTimezone();
  return GetCountryForTimezone(tz) ?? 'US';
}

/**
 * Derives the user's IANA timezone from the browser.
 *
 * @returns The IANA timezone identifier (e.g. 'America/Los_Angeles'), or the system default when detection fails.
 */
export function GetUserGeoTimezone(): string {
  if (typeof Intl === 'undefined') return CommonConstants.Defaults.Timezone;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return CommonConstants.Defaults.Timezone;
  }
}

/**
 * Sets a value in local storage.
 *
 * @param key - Local storage key.
 * @param value - Local storage value.
 */
export function SetLocalStorage(key: string, value: string) {
  localStorage.setItem(key, value);
}

/**
 * Gets a value from local storage.
 *
 * @param key - Local storage key.
 * @returns The value associated with the key, or null if not found.
 */
export function GetLocalStorage(key: string): string | null {
  return localStorage.getItem(key);
}

/**
 * Checks whether an asset source requires an async fetch (data URI or HTTP/HTTPS URL) rather than a synchronous texture-cache lookup.
 *
 * @param source - The asset source string.
 * @returns True if the source is a data URI or HTTP(S) URL.
 */
export function RequiresFetch(source: string): boolean {
  return source.startsWith('data:') || source.startsWith('http://') || source.startsWith('https://');
}

/** Lazily-opened singleton IndexedDB connection. Cached so repeated reads/writes share one handle; reset on open failure so a later attempt may retry. */
let idbConnection: Promise<IDBDatabase> | null = null;

/**
 * Opens (or returns the cached handle for) the shared IndexedDB instance used by `SetIndexedDB` / `GetIndexedDB` / `DeleteIndexedDB`. Creates the object store on first open via the
 * `onupgradeneeded` callback. Resets the singleton on any open error so the next caller may retry after transient failures (e.g. browser private-mode quota races).
 *
 * @returns A promise resolving to the open `IDBDatabase`.
 */
function OpenIndexedDB(): Promise<IDBDatabase> {
  if (idbConnection) return idbConnection;
  idbConnection = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(localConstants.idbDatabaseName, localConstants.idbVersion);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(localConstants.idbStoreName)) {
        db.createObjectStore(localConstants.idbStoreName);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB open blocked'));
  }).catch((err: unknown) => {
    idbConnection = null;
    throw err;
  });
  return idbConnection;
}

/**
 * Reads a string value from the shared IndexedDB key-value store. Async equivalent of `GetLocalStorage` for entries that exceed the synchronous localStorage budget (~5 MB) — for example cached
 * translation payloads. Swallows storage errors (returns null) so callers may treat any failure as a cache miss.
 *
 * @param key - The storage key.
 * @returns The stored string, or null if missing / on error.
 */
export async function GetIndexedDB(key: string): Promise<string | null> {
  try {
    const db = await OpenIndexedDB();
    return await new Promise<string | null>((resolve, reject) => {
      const tx = db.transaction(localConstants.idbStoreName, 'readonly');
      const store = tx.objectStore(localConstants.idbStoreName);
      const req = store.get(key);
      req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : null);
      req.onerror = () => reject(req.error);
    });
  } catch (err: unknown) {
    // Environmental: private mode, no IDB support, quota exceeded. Not patchable client-side and can fire on every cache op for an affected session, so logged as Log to keep error metrics clean.
    Logger.Log(localConstants.logContext, `${localConstants.logIndexedDBGetErr}${Logger.GetErrorMessage(err)}`);
    return null;
  }
}

/**
 * Writes a string value to the shared IndexedDB key-value store. Async equivalent of `SetLocalStorage` for entries that exceed the synchronous localStorage budget. Swallows storage errors (logs
 * and returns) so callers can fire-and-forget cache writes without blocking the UI path on storage I/O.
 *
 * @param key - The storage key.
 * @param value - The string value to persist.
 */
export async function SetIndexedDB(key: string, value: string): Promise<void> {
  try {
    const db = await OpenIndexedDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(localConstants.idbStoreName, 'readwrite');
      const store = tx.objectStore(localConstants.idbStoreName);
      const req = store.put(value, key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err: unknown) {
    // Environmental: see note in GetIndexedDB.
    Logger.Log(localConstants.logContext, `${localConstants.logIndexedDBSetErr}${Logger.GetErrorMessage(err)}`);
  }
}

/**
 * Deletes a key from the shared IndexedDB key-value store. Used to evict corrupt cache entries discovered at read time. Errors are logged but not rethrown so cache eviction never blocks the
 * calling code path.
 *
 * @param key - The storage key to delete.
 */
export async function DeleteIndexedDB(key: string): Promise<void> {
  try {
    const db = await OpenIndexedDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(localConstants.idbStoreName, 'readwrite');
      const store = tx.objectStore(localConstants.idbStoreName);
      const req = store.delete(key);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch (err: unknown) {
    // Environmental: see note in GetIndexedDB.
    Logger.Log(localConstants.logContext, `${localConstants.logIndexedDBDeleteErr}${Logger.GetErrorMessage(err)}`);
  }
}

/** The resolved dashboard noun pair for a tenant's niche (captured callers and appointments). */
export interface INicheLabels {
  leads: string;
  bookings: string;
}

/** Minimal niche-catalog fields needed to resolve the Leads/Bookings nouns (a subset of INicheContentDTO). */
export interface INicheLabelSource {
  allowCustomTerms?: boolean;
  leadsLabel?: string;
  bookingsLabel?: string;
}

/**
 * Reports whether the current page is being rendered by a known crawler/bot. Bots self-identify in the user agent (Googlebot, AdsBot, Bingbot, etc.); their aborted fetches and stale-cache misses
 * are expected rendering noise, not user-facing faults, so callers can log them instead of recording warnings/errors.
 *
 * @returns True when the user agent matches a known bot/crawler.
 */
export function IsBotUserAgent(): boolean {
  if (typeof navigator === 'undefined') return false;
  return localConstants.botUserAgentPattern.test(navigator.userAgent);
}

/**
 * Reports whether an error is a network-level fetch failure (Chrome's `Failed to fetch`, Firefox's `NetworkError ...`, Safari's `Load failed`) rather than a server/HTTP fault - the signature of
 * a crawler aborting a request or a client going offline mid-call. These carry no status code and no server signal, so they are noise for telemetry triage: callers log them instead of recording
 * errors.
 *
 * @param err - The caught error.
 * @returns True for network-level fetch failures.
 */
export function IsBenignNetworkError(err: unknown): boolean {
  return err instanceof TypeError && localConstants.networkErrorPattern.test(err.message);
}

/**
 * Resolves the niche-specific dashboard nouns for captured callers and appointments (e.g. "Patients" / "Appointments" for a clinic). Priority: the tenant's own custom label (only when the niche
 * is flagged `allowCustomTerms`), then the niche's own label, then the caller-supplied generic fallback. Shared by the Lobby nav (`useNicheLabels`) and the Portal Tenant Details page so both
 * surfaces resolve terminology identically. Fallbacks are passed in because they are app-localized strings (`@shared/frontend` cannot import an app's `Localized`).
 *
 * @param niche - The tenant's matched niche-catalog entry (or undefined when none matches).
 * @param customLabels - The tenant's `BusinessProfile.customLabels` map (keyed by CommonConstants.CustomLabels), or null/undefined when absent.
 * @param fallbackLeads - Generic "Leads" noun to use when neither a custom nor a niche label applies.
 * @param fallbackBookings - Generic "Bookings" noun to use when neither a custom nor a niche label applies.
 * @returns The resolved leads/bookings nouns.
 */
export function ResolveNicheLabels(niche: INicheLabelSource | undefined, customLabels: Record<string, string> | null | undefined, fallbackLeads: string, fallbackBookings: string): INicheLabels {
  const custom = niche?.allowCustomTerms ? customLabels : undefined;
  return {
    leads: custom?.[CommonConstants.CustomLabels.Leads] || niche?.leadsLabel || fallbackLeads,
    bookings: custom?.[CommonConstants.CustomLabels.Bookings] || niche?.bookingsLabel || fallbackBookings
  };
}
